import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  TransferOrderReportParams,
  TransferOrderReportRecord,
} from "../../../@types/DRO/AllReportsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { GetAllModelTypeArray } from "../../../@types/DRO/GetPartsListResponse";
import { AllReportsApi } from "../../../api/AllReportsDownload/AllReportsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";

const TransferOrderReport: React.FC = () => {
  // State for data and pagination
  const [data, setData] = useState<TransferOrderReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // State for filters
  const [filters, setFilters] = useState<TransferOrderReportParams>({
    source_location: undefined,
    destination_location: undefined,
    part_code: undefined,
    part_name: "",
    to_no: undefined,
    start_date: getDefaultDates().startDate,
    end_date: getDefaultDates().endDate,
    page: 1,
    page_size: 50,
  });

  // State for dropdowns
  const [sourceWarehouses, setSourceWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [destWarehouses, setDestWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);

  // Selected values
  const [selectedSourceWarehouse, setSelectedSourceWarehouse] = useState<
    GetAllWarehouseApiResponse["data"]["data"][0] | null
  >(null);
  const [selectedDestWarehouse, setSelectedDestWarehouse] = useState<
    GetAllWarehouseApiResponse["data"]["data"][0] | null
  >(null);
  const [selectedOEMType, setSelectedOEMType] =
    useState<GetAllModelTypeArray | null>(null);

  // Pagination and search for dropdowns (separate for source and destination)
  const [sourceWarehousesCurrentPage, setSourceWarehousesCurrentPage] =
    useState(0);
  const [sourceWarehousesSearchQuery, setSourceWarehousesSearchQuery] =
    useState("");
  const [destWarehousesCurrentPage, setDestWarehousesCurrentPage] = useState(0);
  const [destWarehousesSearchQuery, setDestWarehousesSearchQuery] =
    useState("");
  const [hasMoreSourceWarehouses, setHasMoreSourceWarehouses] = useState(true);
  const [hasMoreDestWarehouses, setHasMoreDestWarehouses] = useState(true);
  const rowsPerPage = 10;

  // Fetch functions for dropdowns (separate for source and destination)
  const fetchSourceWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        sourceWarehousesCurrentPage,
        rowsPerPage,
        sourceWarehousesSearchQuery
      );
      setSourceWarehouses((prev) =>
        sourceWarehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreSourceWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching source warehouses", error);
    }
  }, [sourceWarehousesCurrentPage, sourceWarehousesSearchQuery]);

  const fetchDestWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        destWarehousesCurrentPage,
        rowsPerPage,
        destWarehousesSearchQuery
      );
      setDestWarehouses((prev) =>
        destWarehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreDestWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching destination warehouses", error);
    }
  }, [destWarehousesCurrentPage, destWarehousesSearchQuery]);

  // Handle scroll for infinite loading (separate for source and destination)
  const handleSourceScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 1) {
      if (hasMoreSourceWarehouses) {
        setSourceWarehousesCurrentPage((prev) => prev + 1);
      }
    }
  };
  const handleDestScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 1) {
      if (hasMoreDestWarehouses) {
        setDestWarehousesCurrentPage((prev) => prev + 1);
      }
    }
  };

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: TransferOrderReportParams = {
        ...filters,
        source_location: selectedSourceWarehouse?.id || filters.source_location,
        destination_location:
          selectedDestWarehouse?.id || filters.destination_location,
        // oem_type_id: selectedOEMType?.id,
        page: currentPage,
        page_size: pageSize,
      };

      // Remove empty string values
      Object.keys(params).forEach((key) => {
        const k = key as keyof TransferOrderReportParams;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      const response = await AllReportsApi.api.getTransferOrderReport(params);
      if (response.success) {
        setData(response.records);
        setTotalPages(response.total_pages);
      }
    } catch (err) {
      setError("Failed to fetch transfer order report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    currentPage,
    pageSize,
    selectedSourceWarehouse,
    selectedDestWarehouse,
    selectedOEMType,
  ]);

  // Fetch source warehouses
  useEffect(() => {
    const debouncedFetchSourceWarehouses = debounce(fetchSourceWarehouses, 500);
    debouncedFetchSourceWarehouses();
    return () => debouncedFetchSourceWarehouses.cancel();
  }, [fetchSourceWarehouses, sourceWarehousesCurrentPage]);

  // Fetch destination warehouses
  useEffect(() => {
    const debouncedFetchDestWarehouses = debounce(fetchDestWarehouses, 500);
    debouncedFetchDestWarehouses();
    return () => debouncedFetchDestWarehouses.cancel();
  }, [fetchDestWarehouses, destWarehousesCurrentPage]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange =
    (field: keyof TransferOrderReportParams) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFilters({
        ...filters,
        [field]: event.target.value,
      });
      setCurrentPage(1);
    };

  const handleNumericInputChange =
    (field: "part_code" | "to_no") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Allow only numbers and empty string
      if (value === "" || /^[0-9]+$/.test(value)) {
        setFilters({
          ...filters,
          [field]: value === "" ? undefined : parseInt(value, 10),
        });
        setCurrentPage(1);
      }
    };

  // Handle search button click
  const handleSearch = () => {
    fetchData();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const defaultDates = getDefaultDates();
    setFilters({
      source_location: undefined,
      destination_location: undefined,
      part_code: undefined,
      part_name: "",
      to_no: undefined,
      start_date: defaultDates.startDate,
      end_date: defaultDates.endDate,
    });
    setSelectedSourceWarehouse(null);
    setSelectedDestWarehouse(null);
    setSelectedOEMType(null);
    setCurrentPage(1);
    setSourceWarehousesSearchQuery("");
    setDestWarehousesSearchQuery("");
    setSourceWarehousesCurrentPage(0);
    setDestWarehousesCurrentPage(0);
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        source_location: selectedSourceWarehouse?.id || filters.source_location,
        destination_location:
          selectedDestWarehouse?.id || filters.destination_location,
      };
      delete params.page;
      delete params.page_size;
      await AllReportsApi.api.downloadTransferOrderReport(params);
    } catch (err) {
      setError("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Format quantity
  const formatQuantity = (value: number | null) => {
    return value !== null ? value.toLocaleString() : "-";
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#000" }}>
              Transfer Order Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              Track inventory transfers between warehouses with detailed part
              information
            </Typography>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Filter Options
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}>
              <Autocomplete
                fullWidth
                size="small"
                options={sourceWarehouses}
                getOptionLabel={(option) => option.name}
                value={selectedSourceWarehouse}
                onChange={(event, newValue) => {
                  setSelectedSourceWarehouse(newValue);
                  // If destination is same as new source, clear destination
                  if (
                    newValue &&
                    selectedDestWarehouse &&
                    newValue.id === selectedDestWarehouse.id
                  ) {
                    setSelectedDestWarehouse(null);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setSourceWarehousesCurrentPage(0);
                  setSourceWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: handleSourceScroll,
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <Autocomplete
                fullWidth
                size="small"
                options={
                  selectedSourceWarehouse
                    ? destWarehouses.filter(
                        (w) => w.id !== selectedSourceWarehouse.id
                      )
                    : destWarehouses
                }
                getOptionLabel={(option) => option.name}
                value={selectedDestWarehouse}
                onChange={(event, newValue) => {
                  setSelectedDestWarehouse(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setDestWarehousesCurrentPage(0);
                  setDestWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: handleDestScroll,
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <TextField
                label="Reference Code"
                value={filters.part_code || ""}
                onChange={handleFilterChange("part_code")}
                size="small"
                inputProps={{
                  inputMode: "text",
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Part Name"
                value={filters.part_name}
                onChange={handleFilterChange("part_name")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Transfer Order No"
                value={filters.to_no || ""}
                onChange={handleNumericInputChange("to_no")}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              {/* <Autocomplete
                fullWidth
                size="small"
                options={modelTypes}
                getOptionLabel={(option) => option.description}
                value={selectedOEMType}
                onChange={(event, newValue) => {
                  setSelectedOEMType(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="OEM Type"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
              /> */}
              <TextField
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange("start_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange("end_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#000",
                  color: "#000",
                  "&:hover": {
                    borderColor: "#000",
                    bgcolor: "#f5f5f5",
                  },
                }}>
                Clear Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid #000",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#999",
                  },
                }}>
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, border: "1px solid #ff5252" }}
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress sx={{ color: "#000" }} />
                </Box>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>TO No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Source Warehouse
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Destination
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Reference Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Part Name
                        </TableCell>

                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Quantities
                        </TableCell>

                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Quantity Picked
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Quantity In-Transit
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Order Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Original Order Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Received Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row, index) => (
                          <TableRow
                            key={`${row.TO_no}-${index}`}
                            sx={{
                              "&:hover": { bgcolor: "#fafafa" },
                              "&:last-child td": { border: 0 },
                            }}>
                            <TableCell>
                              <Chip
                                label={row.TO_no}
                                size="small"
                                sx={{
                                  bgcolor: "#000",
                                  color: "#fff",
                                }}
                              />
                            </TableCell>
                            <TableCell>{row.source_warehouse_name}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}>
                                <Typography>{row.location}</Typography>
                                {/* <TransferIcon
                                  sx={{ color: "#666", fontSize: 18 }}
                                /> */}
                              </Box>
                            </TableCell>
                            <TableCell>{row.reference_code}</TableCell>
                            <TableCell>
                              <Tooltip title={row.part_name}>
                                <Typography
                                  sx={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}>
                                  {row.part_name}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}>
                                  Ordered:{" "}
                                  {formatQuantity(row.quantity_ordered)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary">
                                  Received:{" "}
                                  {formatQuantity(row.quantity_received)}
                                </Typography>
                                {row.quantity_pending !== null &&
                                  row.quantity_pending > 0 && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      display="block">
                                      Pending:{" "}
                                      {formatQuantity(row.quantity_pending)}
                                    </Typography>
                                  )}
                              </Box>
                            </TableCell>
                            <TableCell>{row.quantity_picked}</TableCell>
                            <TableCell>{row.quantity_in_transit}</TableCell>
                            <TableCell>{row.order_date}</TableCell>
                            <TableCell>{row.original_order_date}</TableCell>
                            <TableCell>{row.received_date}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid #e0e0e0",
                      bgcolor: "#fafafa",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e as any)}
                        size="small"
                        sx={{
                          minWidth: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                        }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "#333",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default TransferOrderReport;
